import { computed, ComputedRef, ref, Ref } from 'vue';

/**
 * Generic wizard step-navigation composable.
 *
 * Owns the current step pointer and provides navigation guards based on
 * per-step completion flags. Feature-specific concerns (step labels,
 * descriptions, completion rules, side-effects on entering/leaving a step)
 * live in the feature's own thin wrapper composable and are passed in here.
 *
 * Designed to be consumed by both `useJointsStepNavigation` and (eventually)
 * a refactored `useCombinationsStepNavigation`.
 */
export interface UseWizardStepNavigationOptions {
    /**
     * Total number of steps in the wizard (1-indexed; `currentStep` ranges
     * from 1 to `totalSteps`). Pass a computed when the step count depends
     * on earlier selections (e.g. a conditional final step).
     */
    totalSteps: number | ComputedRef<number>;

    /**
     * Per-step completion flags, 0-indexed. `stepCompletions.value[stepIndex]`
     * must be `true` before the user is allowed to move forward FROM that step.
     */
    stepCompletions: ComputedRef<boolean[]>;

    /**
     * Optional side-effect invoked AFTER the step pointer advances or retreats
     * into `step`. Use for entry initialisation.
     */
    onEnterStep?: (step: number) => void;

    /**
     * Optional side-effect invoked BEFORE leaving `step`. Receives the step
     * being left and the direction (`'forward'` / `'backward'`). Use for
     * cleaning up values that should not survive a step change.
     */
    onLeaveStep?: (step: number, direction: 'forward' | 'backward', targetStep: number) => void;

    /**
     * Initial step (defaults to 1).
     */
    initialStep?: number;
}

export interface UseWizardStepNavigationReturn {
    currentStep: Ref<number>;
    canGoToPrevStep: ComputedRef<boolean>;
    canGoToNextStep: ComputedRef<boolean>;
    /** Per-step completion flags (re-exposed from options). 0-indexed. */
    stepCompletions: ComputedRef<boolean[]>;
    /** Per-step accessibility: step N is accessible iff steps 1..N-1 are all complete. 0-indexed. */
    stepsAccessible: ComputedRef<boolean[]>;
    goToNextStep: () => void;
    goToPrevStep: () => void;
    /** Jump directly to a step (1-indexed). No-op when target step is inaccessible. */
    goToStep: (step: number) => void;
    resetToFirstStep: () => void;
}

export function useWizardStepNavigation(options: UseWizardStepNavigationOptions): UseWizardStepNavigationReturn {
    const { stepCompletions, onEnterStep, onLeaveStep, initialStep = 1 } = options;

    const totalSteps = computed<number>(() =>
        typeof options.totalSteps === 'number' ? options.totalSteps : options.totalSteps.value,
    );

    const currentStep = ref<number>(initialStep);

    const canGoToPrevStep = computed<boolean>(() => currentStep.value > 1);

    const canGoToNextStep = computed<boolean>(() => {
        if (currentStep.value >= totalSteps.value) {
            return false;
        }

        const completionIndexForCurrentStep = currentStep.value - 1;
        return stepCompletions.value[completionIndexForCurrentStep] === true;
    });

    function goToNextStep() {
        if (!canGoToNextStep.value) {
            return;
        }

        const stepBeingLeft = currentStep.value;
        onLeaveStep?.(stepBeingLeft, 'forward', stepBeingLeft + 1);
        currentStep.value += 1;
        onEnterStep?.(currentStep.value);
    }

    function goToPrevStep() {
        if (!canGoToPrevStep.value) {
            return;
        }

        const stepBeingLeft = currentStep.value;
        onLeaveStep?.(stepBeingLeft, 'backward', stepBeingLeft - 1);
        currentStep.value -= 1;
        onEnterStep?.(currentStep.value);
    }

    const stepsAccessible = computed<boolean[]>(() => {
        const flags: boolean[] = [];
        for (let stepIndex = 0; stepIndex < totalSteps.value; stepIndex += 1) {
            const allPreviousCompleted = stepCompletions.value.slice(0, stepIndex).every(Boolean);
            flags.push(allPreviousCompleted);
        }
        return flags;
    });

    function goToStep(step: number) {
        if (step < 1 || step > totalSteps.value || step === currentStep.value) {
            return;
        }
        if (!stepsAccessible.value[step - 1]) {
            return;
        }

        const direction: 'forward' | 'backward' = step > currentStep.value ? 'forward' : 'backward';
        const stepBeingLeft = currentStep.value;
        onLeaveStep?.(stepBeingLeft, direction, step);
        currentStep.value = step;
        onEnterStep?.(currentStep.value);
    }

    function resetToFirstStep() {
        currentStep.value = initialStep;
    }

    return {
        currentStep,
        canGoToPrevStep,
        canGoToNextStep,
        stepCompletions,
        stepsAccessible,
        goToNextStep,
        goToPrevStep,
        goToStep,
        resetToFirstStep,
    };
}
